import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
	useCreateLiProfileVisitsMutation,
	useGetProfilesQuery,
	useUpdateLiProfileVisitsMutation,
} from "@/pages/Private/redux/liProfileVisits/liProfileVisits.api";

import { ERROR_TYPE, getAllErrors, renderErrorMessages, useSearch } from "@/utils";

import { Button } from "@/components/Button/Button";

import { ButtonColor } from "@/components/Button/types";

import { LiProfileVisitsTimeframe } from "@/enum/liprofile.enum";

import { AutoComplete, AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";

import { Dropdown, AutoCompleteItem as DropdownItem } from "@components/Dropdown/Dropdown";

import { List } from "../../List/schema/list";

import {
	CreateLiProfileVisits,
	CreateLiProfileVisitsSchema,
	LiProfileVisits,
} from "../../LiProfileVisits/schema/liProfileVisits";

interface CreateLiProfileVisitsFormProps {
	liProfileVisits?: LiProfileVisits | null;
	list: List;
	handleSelect?: (company: LiProfileVisits | null) => void;
	handleDuplicate?: (company: LiProfileVisits | null) => void;
}

const initialState = {
	name: "",
	userId: undefined,
	timeframe: LiProfileVisitsTimeframe.DAYS14,
};

export const CreateLiProfileVisitsForm: FunctionComponent<CreateLiProfileVisitsFormProps> = ({
	liProfileVisits,
	list,
	handleSelect,
	handleDuplicate,
}) => {
	const { t } = useTranslation();

	const ts = (key: string) => t(`liProfileVisits.${key}`);
	const [createLiProfileVisits, { error, isLoading }] = useCreateLiProfileVisitsMutation();
	const [updateLiProfileVisits, { error: updateError, isLoading: updateIsLoading }] =
		useUpdateLiProfileVisitsMutation();

	const { query, setSearchValue } = useSearch();

	const { data: profiles } = useGetProfilesQuery({
		page: 1,
		limit: 20,
		searchValue: query,
	});

	const [currentFormState, setCurrentFormState] =
		useState<Partial<CreateLiProfileVisits>>(initialState);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleSaveFormState = async (data: { value: any; name: keyof CreateLiProfileVisits }[]) => {
		const newState = { ...currentFormState };

		data.forEach((item) => {
			newState[item.name] = item.value;
		});

		setCurrentFormState(newState);

		data.forEach((item) => {
			setValue(item.name, item.value, {
				shouldDirty: true,
				shouldValidate: true,
			});
		});
	};

	const {
		handleSubmit,
		setValue,
		reset,
		formState: { errors },
	} = useForm<CreateLiProfileVisits>({
		defaultValues: {
			...initialState,
			listId: list.id,
		},
		resolver: zodResolver(CreateLiProfileVisitsSchema),
	});

	useEffect(() => {
		if (liProfileVisits) {
			const newInitialState = {
				name: liProfileVisits.name,
				userId: liProfileVisits.userId,
				listId: list.id,
				timeframe: liProfileVisits.timeframe,
			};

			if (handleDuplicate) {
				reset({ ...newInitialState });
				setCurrentFormState({
					...newInitialState,
				});
			} else {
				reset({
					id: liProfileVisits.id,
					...newInitialState,
				});
				setCurrentFormState({
					...newInitialState,
					id: liProfileVisits.id,
				});
			}
		} else {
			reset();
		}
	}, [list.scrapeLevel, liProfileVisits, reset, handleDuplicate, list.id]);

	const onSubmit = async (values: CreateLiProfileVisits) => {
		try {
			if (liProfileVisits?.id && !handleDuplicate) {
				await updateLiProfileVisits({
					...values,
					id: liProfileVisits.id,
					listId: list.id,
				}).unwrap();
			} else {
				await createLiProfileVisits({
					...values,
					listId: list.id,
				}).unwrap();
			}

			reset(initialState);

			handleSelect?.(null);
			handleDuplicate?.(null);
			setCurrentFormState(initialState);
		} catch (err) {
			console.error(err);
		}
	};

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];
	const buttonTitle =
		handleDuplicate && liProfileVisits?.id
			? t("basics.copyCriteria")
			: liProfileVisits?.id
				? t("basics.save")
				: t("basics.addCriteria");

	return (
		<div className="p-4 mt-8 rounded-xl border border-gray-200 bg-brand-white">
			<div className="text-md mb-5">Add a source</div>
			<div className="flex flex-row gap-4 w-full mb-4">
				<AutoComplete
					data={
						profiles?.data?.map((c) => {
							return {
								id: c.id,
								title: c.firstName + " " + c.lastName,
							} as AutoCompleteItem;
						}) || []
					}
					handleSearch={(value: string) => setSearchValue(value)}
					handleSelect={async (item?: AutoCompleteItem) => {
						await handleSaveFormState([
							{ value: item?.id, name: "userId" },
							{ value: item?.title, name: "name" },
						]);
					}}
					label={ts("profile")}
					selectedItem={currentFormState.name || ""}
				/>
			</div>

			<div className="flex flex-row gap-4 w-full mb-4">
				<Dropdown
					data={[
						{
							title: ts(LiProfileVisitsTimeframe.DAYS14),
							id: LiProfileVisitsTimeframe.DAYS14,
						},
						{
							title: ts(LiProfileVisitsTimeframe.DAYS28),
							id: LiProfileVisitsTimeframe.DAYS28,
						},
						{
							title: ts(LiProfileVisitsTimeframe.DAYS90),
							id: LiProfileVisitsTimeframe.DAYS90,
						},
						{
							title: ts(LiProfileVisitsTimeframe.DAYS365),
							id: LiProfileVisitsTimeframe.DAYS365,
						},
					]}
					defaultValue={{
						title: ts(LiProfileVisitsTimeframe.DAYS14),
						id: LiProfileVisitsTimeframe.DAYS14,
					}}
					floating={true}
					handleSelect={async (value: DropdownItem) => {
						if (value?.id && value?.id !== currentFormState.timeframe) {
							await handleSaveFormState([{ value: value?.id, name: "timeframe" }]);
						}
					}}
					label={ts("timeframe")}
					value={
						currentFormState?.timeframe
							? {
									title: ts(currentFormState.timeframe),
									id: currentFormState.timeframe,
								}
							: undefined
					}
				/>
			</div>

			<div className="flex flex-row gap-4 w-full mb-4">
				{getAllErrors(error || updateError, formErrors).length
					? renderErrorMessages(getAllErrors(error || updateError, formErrors))
					: null}
			</div>
			<div className="flex gap-4 w-full justify-end">
				{handleSelect && (
					<div className="flex max-w-[100px] w-full">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							title="cancel"
							onClick={() => {
								reset(initialState);

								setCurrentFormState(initialState);

								handleSelect?.(null);
								handleDuplicate?.(null);
							}}
						/>
					</div>
				)}

				<div className="flex max-w-[150px] w-full">
					<Button
						isLoading={isLoading || updateIsLoading}
						title={buttonTitle}
						onClick={handleSubmit(onSubmit)}
					/>
				</div>
			</div>
		</div>
	);
};
